import { select, scaleBand, selectAll } from "d3";
import { nest } from "d3-collection";
import { gainsboro, whisper } from "../../common/helpers/colors";

const cellMouseout = (tooltip) => {
  selectAll("*").classed("highlighted", false);
  tooltip.hide();
};

const cellMouseover = (event, d, dom, selected, tooltip) => {
  const rowClass = selected.attr("row");
  const colClass = selected.attr("col");
  dom
    .selectAll(".exp-map-xlabel")
    .filter(`.${rowClass}`)
    .classed("highlighted", true);
  dom
    .selectAll(".exp-map-ylabel")
    .filter(`.${colClass}`)
    .classed("highlighted", true);
  selected.classed("highlighted", true);
  const displayValue = d.value ? d.value.toPrecision(4) : d.value;
  tooltip.show(
    event,
    `Exon: ${d.exon} <br/> Tissue: ${d.y} <br/> Median read count per base: ${displayValue} <br/>`
  );
};

export const draw = (
  dom,
  dimensions = { w: 1000, h: 600 },
  angle = 90,
  useNullColor = false,
  columnLabelPosAdjust = null,
  heatmapData,
  tooltip,
  colorScale,
  r,
  rowLabelWidth
) => {
  const xList = nest()
    .key((d) => d.x)
    .entries(heatmapData)
    .map((d) => d.key);

  const xScale = scaleBand()
    .domain(xList)
    .range([rowLabelWidth, rowLabelWidth + dimensions.w - 10])
    .padding(0.05);

  const yList = nest()
    .key((d) => d.y)
    .entries(heatmapData)
    .map((d) => d.key);

  const yScale = scaleBand()
    .domain(yList)
    .range([0, dimensions.h])
    .padding(0.05);

  const xLabels = dom.selectAll(".exp-map-xlabel").data(xList);

  const Y =
    columnLabelPosAdjust == null
      ? yScale.range()[1] + yScale.bandwidth() * 2
      : yScale.range()[1] + columnLabelPosAdjust;

  const adjust = 5;

  xLabels.attr("transform", (d) => {
    const x = xScale(d) + adjust;
    const y = Y;
    return `translate(${x}, ${y}) rotate(${angle})`;
  });

  xLabels
    .enter()
    .append("text")
    .attr("class", (d, i) => `exp-map-xlabel x${i}`)
    .attr("x", 0)
    .attr("y", 0)
    .attr("text-anchor", "start")
    .style("cursor", "default")
    .attr("transform", (d) => {
      const x = xScale(d) + adjust;
      const y = Y;
      return `translate(${x}, ${y}) rotate(${angle})`;
    })
    .merge(xLabels)
    .text((d) => d);

  xLabels.exit().remove();

  dom
    .selectAll(".exp-map-ylabel")
    .data(yList)
    .enter()
    .append("text")
    .text((d) => d)
    .attr("y", (d) => yScale(d) + 10)
    .attr("class", (d, i) => `exp-map-ylabel y${i}`)
    .attr("text-anchor", "start")
    .style("cursor", "default")
    .on("mouseover", () => {
      select().classed("normal", false).classed("highlighted", true);
    })
    .on("mouseout", () => {
      select().classed("normal", true).classed("highlighted", false);
    });

  const cells = dom
    .selectAll(".exp-map-cell")
    .data(heatmapData, (d) => d.value);

  cells
    .attr("x", (d) => xScale(d.x))
    .attr("y", (d) => yScale(d.y))
    .attr("row", (d) => `x${xList.indexOf(d.x)}`)
    .attr("col", (d) => `y${yList.indexOf(d.y)}`);

  cells
    .enter()
    .append("rect")
    .attr("row", (d) => `x${xList.indexOf(d.x)}`)
    .attr("col", (d) => `y${yList.indexOf(d.y)}`)
    .attr("x", (d) => xScale(d.x))
    .attr("y", (d) => yScale(d.y))
    .attr("rx", r)
    .attr("ry", r)
    .attr("class", "exp-map-cell")
    .attr("width", xScale.bandwidth())
    .attr("height", yScale.bandwidth())
    .style("fill", whisper)
    .on("mouseover", (event, d) => {
      const selected = select(event.target);
      cellMouseover(event, d, dom, selected, tooltip);
    })
    .on("mouseout", () => {
      cellMouseout(tooltip);
    })
    .merge(cells)
    .style("fill", (d) => {
      return useNullColor && d.value === 0 ? gainsboro : colorScale(d.value);
    });

  cells.exit().remove();
};
